import React, { useState } from "react";
import { TOKENS } from "../tokens";
import { useClock, fmtTime } from "../hooks/useClock";
import { useIsMobile } from "../hooks/useIsMobile";
import { useApp } from "../context/AppContext";
import { DashboardScreen } from "./DashboardScreen";
import { CheckInScreen } from "./CheckInScreen";
import { TasksScreen } from "./TasksScreen";
import { ReimbursementScreen } from "./ReimbursementScreen";
import { ProfileScreen } from "./ProfileScreen";
import { EverydayReportScreen } from "./EverydayReportScreen";
import { getImageUrl } from "../api/client";
import { Home, MapPin, FileText, ListChecks, Wallet, User, LogOut, Clock, CalendarDays, Bell, CheckCheck, AlertCircle, Menu, X } from "lucide-react";
import { LeaveScreen } from "./LeaveScreen";
import { IncidentScreen } from "./IncidentScreen";

const TABS = [
  { id: "dashboard",     label: "Home",     Icon: Home },
  { id: "checkin",       label: "Check In", Icon: MapPin },
  { id: "leave",         label: "Leave",    Icon: CalendarDays },
  { id: "report",        label: "Report",   Icon: FileText },
  { id: "tasks",         label: "Tasks",    Icon: ListChecks },
  { id: "reimbursement", label: "Reimbursement",  Icon: Wallet },
  { id: "incident",      label: "Incidents",Icon: AlertCircle },
  { id: "profile",       label: "Profile",  Icon: User },
];

export function EmployeeApp() {
  const { employee: emp, error, logout, notifications, unreadNotifCount, markNotificationsRead } = useApp();
  const [tab, setTab] = useState("dashboard");
  const now = useClock();
  const isMobile = useIsMobile();
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  if (error) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16, background: TOKENS.cream, padding: 20, textAlign: "center" }}>
        <div style={{ color: "#ef4444", fontSize: 16, fontWeight: 700 }}>Error Loading Profile</div>
        <div style={{ fontSize: 14, color: TOKENS.muted }}>{error}</div>
        <button onClick={logout} style={{ marginTop: 20, padding: "10px 20px", background: TOKENS.navyDeep, color: "#fff", borderRadius: 8, fontWeight: 600, border: "none" }}>
          Go Back
        </button>
      </div>
    );
  }

  if (!emp) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16, background: TOKENS.cream }}>
        <div style={{ width: 48, height: 48, border: `4px solid ${TOKENS.goldPale}`, borderTop: `4px solid ${TOKENS.gold}`, borderRadius: "50%", animation: "spin 1s linear infinite" }} />
        <div style={{ fontSize: 14, color: TOKENS.navyDeep, fontWeight: 600 }}>Loading employee profile…</div>
      </div>
    );
  }

  // ── MOBILE LAYOUT ──────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <div style={{ display: "flex", flexDirection: "column", height: "100vh", width: "100vw", background: TOKENS.bgPage, overflow: "hidden" }}>
        {/* Mobile Top Header */}
        <div style={{
          background: `linear-gradient(135deg, ${TOKENS.navyDeep}, ${TOKENS.navySoft})`,
          padding: "12px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
          boxShadow: "0 2px 12px rgba(10,25,50,0.2)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {emp.selfie ? (
              <img src={getImageUrl(emp.selfie)} alt="selfie" style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover", border: `2px solid ${TOKENS.gold}` }} />
            ) : (
              <div style={{ width: 32, height: 32, borderRadius: "50%", background: TOKENS.navySoft, border: `2px solid ${TOKENS.gold}`, display: "flex", alignItems: "center", justifyContent: "center", color: TOKENS.gold, fontSize: 13, fontWeight: 700 }}>
                {emp.initials}
              </div>
            )}
            <div>
              <div style={{ fontFamily: "Fraunces, serif", fontSize: 15, fontWeight: 700, color: "#fff", lineHeight: 1 }}>{emp.name}</div>
              <div style={{ fontSize: 10, color: TOKENS.goldLight, fontWeight: 600, letterSpacing: 0.5 }}>{emp.role}</div>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ fontSize: 11, color: "#C7D2E3", display: "flex", alignItems: "center", gap: 4 }}>
              <Clock size={12} color={TOKENS.gold} />
              <span style={{ fontWeight: 700, color: TOKENS.gold }}>{fmtTime(now)}</span>
            </div>

            {/* Notification Bell */}
            <button
              onClick={() => {
                setIsNotifOpen(true);
                markNotificationsRead();
              }}
              title="Notifications"
              style={{
                background: "rgba(245,184,0,0.15)", border: `1px solid ${TOKENS.gold}50`,
                borderRadius: 8, padding: "6px 8px", display: "flex", alignItems: "center", justifyContent: "center",
                color: TOKENS.gold, cursor: "pointer", position: "relative"
              }}
            >
              <Bell size={13} />
              {unreadNotifCount > 0 && (
                <span style={{
                  position: "absolute", top: -4, right: -4, background: "#ef4444", color: "#fff",
                  fontSize: 9, fontWeight: 800, borderRadius: 10, padding: "1px 5px", minWidth: 16, textAlign: "center"
                }}>
                  {unreadNotifCount}
                </span>
              )}
            </button>

            <button onClick={logout} style={{
              background: "rgba(232,137,111,0.15)", border: "1px solid rgba(232,137,111,0.3)",
              borderRadius: 8, padding: "6px 10px", display: "flex", alignItems: "center",
              color: "#E8896F", fontSize: 12, fontWeight: 700, cursor: "pointer",
            }}>
              <LogOut size={13} />
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 14px 80px", WebkitOverflowScrolling: "touch" }}>
          <div key={tab} className="screen-enter">
            {tab === "dashboard"     && <DashboardScreen     emp={emp} goTab={setTab} />}
            {tab === "checkin"       && <CheckInScreen       emp={emp} />}
            {tab === "leave"         && <LeaveScreen         emp={emp} />}
            {tab === "report"        && <EverydayReportScreen />}
            {tab === "tasks"         && <TasksScreen         emp={emp} />}
            {tab === "reimbursement" && <ReimbursementScreen emp={emp} />}
            {tab === "incident"      && <IncidentScreen      emp={emp} />}
            {tab === "profile"       && <ProfileScreen       emp={emp} />}
          </div>
        </div>

        {/* Mobile Bottom Navigation */}
        <div style={{
          position: "fixed", bottom: 0, left: 0, right: 0,
          background: "#fff",
          borderTop: `1px solid ${TOKENS.border}`,
          display: "flex",
          justifyContent: "space-between",
          boxShadow: "0 -4px 20px rgba(10,25,50,0.10)",
          zIndex: 100,
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
        }}>
          {TABS.slice(0, 4).map((t) => {
            const { id, label, Icon, action } = t;
            const active = tab === id;
            return (
              <button
                key={id}
                onClick={() => {
                  if (action) action();
                  else setTab(id);
                  setIsMobileMenuOpen(false);
                }}
                style={{
                  flex: 1, display: "flex", flexDirection: "column",
                  alignItems: "center", justifyContent: "center",
                  padding: "10px 4px 8px",
                  border: "none", background: "none", cursor: "pointer",
                  color: active ? TOKENS.navyDeep : TOKENS.muted,
                  position: "relative",
                }}
              >
                {active && (
                  <div style={{
                    position: "absolute", top: 0, left: "50%", transform: "translateX(-50%)",
                    width: 28, height: 3, borderRadius: 2, background: TOKENS.navyDeep,
                  }} />
                )}
                <Icon size={20} color={active ? TOKENS.navyDeep : TOKENS.muted} />
                <span style={{ fontSize: 10, fontWeight: active ? 700 : 500, marginTop: 3, letterSpacing: 0.2, whiteSpace: "nowrap" }}>
                  {label}
                </span>
              </button>
            );
          })}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            style={{
              flex: 1, display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center",
              padding: "10px 4px 8px",
              border: "none", background: "none", cursor: "pointer",
              color: isMobileMenuOpen ? TOKENS.navyDeep : TOKENS.muted,
              position: "relative",
            }}
          >
            {isMobileMenuOpen && (
              <div style={{
                position: "absolute", top: 0, left: "50%", transform: "translateX(-50%)",
                width: 28, height: 3, borderRadius: 2, background: TOKENS.navyDeep,
              }} />
            )}
            <Menu size={20} color={isMobileMenuOpen ? TOKENS.navyDeep : TOKENS.muted} />
            <span style={{ fontSize: 10, fontWeight: isMobileMenuOpen ? 700 : 500, marginTop: 3, letterSpacing: 0.2, whiteSpace: "nowrap" }}>
              Menu
            </span>
          </button>
        </div>

        {/* Mobile Full Menu Modal */}
        {isMobileMenuOpen && (
          <div style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 60,
            background: "#fff", zIndex: 99, display: "flex", flexDirection: "column",
            boxShadow: "0 4px 20px rgba(0,0,0,0.15)"
          }}>
            <div style={{ padding: "20px 16px", borderBottom: `1px solid ${TOKENS.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", background: TOKENS.cream }}>
              <h3 style={{ margin: 0, fontSize: 18, color: TOKENS.navyDeep, fontFamily: "Fraunces, serif" }}>Menu</h3>
              <button onClick={() => setIsMobileMenuOpen(false)} style={{ background: "none", border: "none", color: TOKENS.muted, fontSize: 20, display: "flex" }}><X size={24} /></button>
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: "24px 16px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, alignContent: "start", background: TOKENS.bgPage }}>
              {TABS.map((t) => {
                const { id, label, Icon, action } = t;
                const active = tab === id;
                return (
                  <button
                    key={id}
                    onClick={() => {
                      if (action) action();
                      else setTab(id);
                      setIsMobileMenuOpen(false);
                    }}
                    style={{
                      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12,
                      padding: "20px 12px", borderRadius: 16, border: `1.5px solid ${active ? TOKENS.gold : TOKENS.border}`,
                      background: active ? `${TOKENS.gold}15` : "#fff",
                      color: active ? TOKENS.navyDeep : TOKENS.ink,
                      cursor: "pointer", boxShadow: "0 4px 12px rgba(0,0,0,0.03)"
                    }}
                  >
                    <Icon size={28} color={active ? TOKENS.gold : TOKENS.navySoft} />
                    <span style={{ textAlign: "center", fontSize: 13, fontWeight: 700, lineHeight: 1.2 }}>{label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── DESKTOP LAYOUT (unchanged) ─────────────────────────────────────────────
  return (
    <div style={{ display: "flex", height: "100vh", width: "100vw", overflow: "hidden", background: TOKENS.cream }}>
      <div style={{ width: 280, background: `linear-gradient(180deg, ${TOKENS.navyDeep}, ${TOKENS.navySoft})`, color: "#fff", display: "flex", flexDirection: "column", borderRight: `1px solid ${TOKENS.border}15`, boxShadow: "4px 0 24px rgba(10,25,50,0.12)", flexShrink: 0 }}>
        <div style={{ padding: "28px 24px", borderBottom: `1px solid ${TOKENS.border}15`, flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <img src="/goldpe-logo.png" alt="GoldPE Logo" className="logo-glow" style={{ width: 38, height: 38, borderRadius: 10, objectFit: "contain", background: "#fff", padding: 2, boxShadow: `0 4px 12px ${TOKENS.gold}33` }} />
            <div>
              <div style={{ fontFamily: "Fraunces, serif", fontSize: 20, fontWeight: 700, color: "#fff", lineHeight: 1.1 }}>GoldPE WIBIL</div>
            </div>
          </div>
        </div>
        <div style={{ padding: "24px 16px", display: "flex", flexDirection: "column", gap: 6, flex: 1, overflowY: "auto" }}>
            {TABS.map((t) => {
              const { id, label, Icon, action } = t;
              const active = tab === id;
              return (
                <button key={id} onClick={() => action ? action() : setTab(id)} style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", padding: "12px 16px", borderRadius: 12, border: "none", background: active ? `${TOKENS.gold}18` : "transparent", color: active ? TOKENS.gold : "#9FB0C9", fontFamily: "Inter, sans-serif", fontSize: 14, fontWeight: active ? 700 : 500, textAlign: "left", cursor: "pointer", transition: "all 0.15s ease" }} className="sidebar-tab-btn">
                  <Icon size={18} color={active ? TOKENS.gold : "#8C9DB5"} />
                  <span style={{ flex: 1 }}>{label}</span>
                  {active && <span style={{ width: 5, height: 5, borderRadius: "50%", background: TOKENS.gold }} />}
                </button>
              );
            })}
          </div>
        <div style={{ padding: "20px 16px", borderTop: `1px solid ${TOKENS.border}15`, background: "rgba(10,21,45,0.25)", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#C7D2E3", fontSize: 12, marginBottom: 14, paddingLeft: 8 }}>
            <Clock size={14} color={TOKENS.gold} />
            <span>Shift clock: <b>{fmtTime(now)}</b></span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14, paddingLeft: 4 }}>
            {emp.selfie ? (
              <img src={getImageUrl(emp.selfie)} alt="selfie" style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover", border: `2.5px solid ${TOKENS.gold}` }} />
            ) : (
              <div style={{ width: 40, height: 40, borderRadius: "50%", background: TOKENS.navySoft, border: `2.5px solid ${TOKENS.gold}`, display: "flex", alignItems: "center", justifyContent: "center", color: TOKENS.gold, fontSize: 14, fontWeight: 700 }}>{emp.initials}</div>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{emp.name}</div>
              <div style={{ fontSize: 11, color: "#8C9DB5", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{emp.role}</div>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <button
              onClick={() => {
                setIsNotifOpen(true);
                markNotificationsRead();
              }}
              style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%", padding: "10px 16px", borderRadius: 10, border: `1px solid ${TOKENS.gold}40`, background: `${TOKENS.gold}15`, color: TOKENS.gold, fontSize: 12.5, fontWeight: 700, cursor: "pointer", transition: "all 0.15s ease", position: "relative" }}
            >
              <Bell size={14} /> Notifications
              {unreadNotifCount > 0 && (
                <span style={{ background: "#ef4444", color: "#fff", fontSize: 9, fontWeight: 800, borderRadius: 10, padding: "1px 6px", marginLeft: 4 }}>
                  {unreadNotifCount}
                </span>
              )}
            </button>
            <button onClick={logout} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%", padding: "10px 16px", borderRadius: 10, border: `1px solid rgba(255,255,255,0.15)`, background: "rgba(255,255,255,0.05)", color: "#E8896F", fontSize: 12.5, fontWeight: 700, cursor: "pointer", transition: "all 0.15s ease" }} className="sidebar-logout">
              <LogOut size={14} /> Sign Out
            </button>
          </div>
        </div>
      </div>
      <div style={{ flex: 1, height: "100vh", overflowY: "auto", padding: "32px 48px", background: TOKENS.bgPage }}>
        <div style={{ maxWidth: 1080, margin: "0 auto" }}>
          <div key={tab} className="screen-enter">
            {tab === "dashboard"     && <DashboardScreen     emp={emp} goTab={setTab} />}
            {tab === "checkin"       && <CheckInScreen       emp={emp} />}
            {tab === "leave"         && <LeaveScreen         emp={emp} />}
            {tab === "report"        && <EverydayReportScreen />}
            {tab === "tasks"         && <TasksScreen         emp={emp} />}
            {tab === "reimbursement" && <ReimbursementScreen emp={emp} />}
            {tab === "incident"      && <IncidentScreen      emp={emp} />}
            {tab === "profile"       && <ProfileScreen       emp={emp} />}
          </div>
        </div>
      </div>

      {/* Notifications Drawer Modal */}
      {isNotifOpen && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "flex-start", justifyContent: "center",
          zIndex: 1000, padding: 16, paddingTop: 60
        }}>
          <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 480, maxHeight: "80vh", display: "flex", flexDirection: "column", padding: 20, boxShadow: "0 10px 25px rgba(0,0,0,0.2)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, paddingBottom: 10, borderBottom: `1px solid ${TOKENS.border}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Bell size={18} color={TOKENS.navyDeep} />
                <h3 style={{ fontFamily: "Fraunces, serif", fontSize: 18, fontWeight: 700, color: TOKENS.navyDeep, margin: 0 }}>
                  Notifications & Alerts
                </h3>
              </div>
              <button onClick={() => setIsNotifOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: TOKENS.muted }}>✕</button>
            </div>

            <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 10 }}>
              {notifications.length === 0 ? (
                <div style={{ textAlign: "center", padding: 32, color: TOKENS.muted, fontSize: 13 }}>
                  🔔 No notifications yet.
                </div>
              ) : (
                notifications.map((n) => (
                  <div key={n.id} style={{
                    padding: 12, borderRadius: 10,
                    border: `1px solid ${n.is_read ? TOKENS.border : TOKENS.gold}`,
                    background: n.is_read ? "#FAFBFD" : `${TOKENS.gold}12`,
                    transition: "all 0.15s ease"
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: TOKENS.navyDeep }}>{n.title}</span>
                      <span style={{ fontSize: 10, color: TOKENS.muted }}>
                        {n.created_at ? String(n.created_at).slice(0, 16).replace("T", " ") : ""}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: TOKENS.ink, lineHeight: 1.4 }}>{n.body}</div>
                  </div>
                ))
              )}
            </div>

            <div style={{ marginTop: 14, paddingTop: 10, borderTop: `1px solid ${TOKENS.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <button
                onClick={() => markNotificationsRead()}
                style={{ background: "none", border: "none", color: TOKENS.navyDeep, fontSize: 12, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}
              >
                <CheckCheck size={14} /> Mark All as Read
              </button>
              <button
                onClick={() => setIsNotifOpen(false)}
                style={{ background: TOKENS.navyDeep, color: TOKENS.gold, border: "none", borderRadius: 8, padding: "8px 16px", fontWeight: 700, fontSize: 12, cursor: "pointer" }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
